/**
 * LLS-520: the shared `POST api/mortgages/urla2020/{mortgageId}` save no longer
 * treats an omitted row as a delete — for assets, REOs and liabilities the API
 * preserves anything missing from the payload and only deletes rows that carry
 * an explicit `entityState: 'Deleted'` marker.
 *
 * So a row the borrower removed has to be SENT with that marker instead of
 * being dropped from its array. A row that was never persisted (id <= 0, or no
 * id at all) has nothing to delete server-side and is simply removed.
 *
 * Because deleted rows now stay in memory until the save response refreshes the
 * cache, every reader (lists, totals, validity checks) has to skip them —
 * `isActive` / `activeOnly` exist for that.
 */

export const DELETED_ENTITY_STATE = 'Deleted';

export interface EntityStateAware {
  entityState?: string;
  requireEntityStateForChanges?: boolean;
}

export function isDeleted(entity: EntityStateAware | null | undefined): boolean {
  return entity?.entityState === DELETED_ENTITY_STATE;
}

export function isActive(entity: EntityStateAware | null | undefined): boolean {
  return !!entity && !isDeleted(entity);
}

export function activeOnly<T extends EntityStateAware>(entities: T[] | null | undefined): T[] {
  return (entities ?? []).filter(isActive);
}

function isPersisted(id: number | null | undefined): boolean {
  return id != null && id > 0;
}

/**
 * Removes `entity` from `entities` in place when it was never persisted, or
 * marks it deleted (keeping it in the array so the save carries the marker)
 * when it has a real backend id. The array reference is kept so bound child
 * components keep pointing at the same collection.
 */
export function removeOrMarkDeleted<T extends EntityStateAware>(
  entities: T[] | null | undefined,
  entity: T,
  id: number | null | undefined
): void {
  if (!entities || !entity) {
    return;
  }
  if (isPersisted(id)) {
    entity.entityState = DELETED_ENTITY_STATE;
    entity.requireEntityStateForChanges = true;
    return;
  }
  const index = entities.indexOf(entity);
  if (index >= 0) {
    entities.splice(index, 1);
  }
}

/**
 * Clears a delete marker so a row the borrower brought back is saved again
 * instead of being deleted (e.g. toggling an address back to "own").
 */
export function undoDelete(entity: EntityStateAware | null | undefined): void {
  if (isDeleted(entity)) {
    entity!.entityState = undefined;
    entity!.requireEntityStateForChanges = undefined;
  }
}

/** `removeOrMarkDeleted` for a set of rows sharing an id accessor. */
export function removeOrMarkDeletedAll<T extends EntityStateAware>(
  entities: T[] | null | undefined,
  toRemove: T[],
  getId: (entity: T) => number | null | undefined
): void {
  toRemove.forEach(entity => removeOrMarkDeleted(entities, entity, getId(entity)));
}
